import { ObjectHandleRepository } from 'util/util';
import { 
	IShareableService,
	ServerLinkShare, 
	LinkShare,
	ReceivedLinkShare,
	CreatedLinkShare,
	RightsRestrictions
} from 'sharebase/domain';
import {
	DocumentRevision,
	DocumentShare,
	ShareBaseFolder,
	ShareBaseDocument,
	ObjectType,
	ReceivedShareInfo,
	DocumentView
} from 'phoenix/domain';
import { DocViewerFormat } from 'phoenix/components/documentViewer/documentViewer';
import SettingsService from 'phoenix/services/settingsService';
import { IUriPathService } from 'phoenix/services/uriPathService';
import { UploadService } from 'phoenix/services/uploadService';

export interface IDocumentService extends IShareableService {
	getDocument(docId:number): ng.IPromise<DocumentView>;
	getContent(document:DocumentView, viewFormat?:DocViewerFormat):ng.IPromise<string>;
	renameDocument(doc:ShareBaseDocument, docName:string): ng.IPromise<ShareBaseDocument>;
	moveDocument(source: any, destination: any, copy?: boolean): ng.IPromise<ShareBaseDocument>;
	deleteDocument(doc:ShareBaseDocument, purge?: boolean): ng.IPromise<any>;
	replaceDocument(document:ShareBaseDocument, data:Blob):ng.IPromise<ShareBaseDocument>;
	createDocument(name:string, parentFolder:ShareBaseFolder):ng.IPromise<ShareBaseDocument>;
	getMediaInfo(doc:ShareBaseDocument): ng.IPromise<any>;
	deleteRevision(rev:DocumentRevision): ng.IPromise<any>;
	promote(rev:DocumentRevision): ng.IPromise<any>;
	getDocumentUri(document:DocumentView, page:number, section?:number, viewFormat?:DocViewerFormat, maxWidth?:number):ng.IPromise<string>;
	getThumbnailUri(document:ShareBaseDocument, size?:number):ng.IPromise<string>;
}

/**
* @ngdoc service
* @name  sharebase.sbDocumentService
* 
* @description 
* A service used to retrieve and interact with ShareBase
* documents
**/

export function DocumentServiceFactory(
	$http:ng.IHttpService, 
	$q:ng.IQService, 
	objectHandleRepo:ObjectHandleRepository,
	settingsService:SettingsService,
	pxUriPathService:IUriPathService,
	pxUploadService:UploadService, 
	
) {
	let documentService:IDocumentService = {
		/**
		* @ngdoc method
		* @name sbDocumentService#getDocument
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Gets a specific document to the user
		* 
		* @param {object} documentId A Document ID
		*
		* @returns {Deferred} A promise that will resolve with a document
		* 
		*/
		getDocument,
		/**
		* @ngdoc method
		* @name sbDocumentService#renameDocument
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Renames the specified document  
		* 
		* @param {item} document A Document object
		* @param {string} documentName A Document name
		*
		* @returns {Deferred} A promise that will resolve if the document is successfully renamed.
		* 
		*/
		renameDocument,
		/**
		* @ngdoc method
		* @name sbDocumentService#deleteDocument
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Delete the specified document
		* 
		* @param {item} document A Document object
		* 
		* @param {bool} purge Will purge the document with the specified ID if true.
		*
		* @returns {Deferred} A promise that will resolve if the document is successfully deleted.
		* 
		*/
		deleteDocument,
		replaceDocument,
		createDocument,
		/**
		* @ngdoc method
		* @name sbDocumentService#getShares
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Retrieves shares for the specified document
		*
		* @param {document} document The document to get shares for.
		*
		* @returns {Deferred} A promise resolving with an array of the retrieved shares.
		* 
		*/
		getShares,
		/**
		* @ngdoc method
		* @name sbDocumentService#getLinks
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Gets a document's share links
		* 
		* @param {object} document A Document 
		*
		* @returns {Deferred} A promise that will resolve with a document's link shares
		* 
		*/
		getLinks,
		getLinkRights,
		/**
		* @ngdoc method
		* @name sbDocumentService#getMediaInfo
		* @methodOf sharebase.sbDocumentService
		* @kind function
		*
		* @description
		* Gets a specific document media information
		* 
		* @param {object} document A Document
		*
		* @returns {Deferred} A promise that will resolve with a document's media info
		* 
		*/
		getMediaInfo,
		/**
		* @ngdoc method
		* @name sbadminDocumentService#getPermissionsForCurrentUser
		* @methodOf sharebase-admin.sbadminDocumentService
		* @kind function
		*
		* @description
		* Retrieves permissions for the current user for the specified document.
		*
		* @param {object} document The document to get permissions for.
		*
		* @returns {Deferred} A promise resolving with an array of the retrieved permissions.
		* 
		*/
		getPermissionsForCurrentUser,
		/**
		 * @ngdoc method
		 * @name sbDocumentService#moveDocument
		 * @methodOf sharebase.sbDocumentService
		 * @kind function
		 *
		 * @description
		 * Move a document to a specified folder.
		 *
		 * @param {Object} source The source document to moveDocument
		 * @param {Object} destination The destination folder to move the document to.
		 * @returns {Deferred} A promise that will resolve to an Angular HTTP response object.
		 */
			moveDocument,
		/**
		 * @ngdoc method
		 * @name sbDocumentService#createLink
		 * @methodOf sharebase.sbDocumentService
		 * @kind function
		 *
		 * @description
		 * Creates a share link to a specified document.
		 *
		 * @param {object} document The document to create a link for
		 * @param {Object} advanced The advanced options for the new link
		 * @returns {Deferred} A promise that will resolve to an Angular HTTP response object.
		 */
			createLink,
			revokeLink,
			/**
			 * @ngdoc method
			 * @name sbDocumentService#deleteRevision
			 * @methodOf sharebase.sbDocumentService
			 * @kind function
			 *
			 * @description
			 * Delete a single file revision associated with a document.
			 *
			 * @param {DocumentRevision} rev The revision to delete
			 * @returns {Deferred} A promise that resolves to an Angular HTTP response.
			 */
			deleteRevision,

			/**
			 * @ngdoc method
			 * @name sbDocumentService#promote
			 * @methodOf sharebase.sbDocumentService
			 * @kind function
			 *
			 * @description
			 * Promotes a document revision to the current revision.
			 *
			 * @param {DocumentRevision} rev The revision to promote
			 * @returns {Deferred} A promise that resolves to the document after the revision
			 * has been promoted.
			 */
			promote,
			getRights,
			createShare,
			updateShare,
			deleteShare,
			getDocumentUri,
			getThumbnailUri,
			getContent,
			requestPermissions: (document:ShareBaseDocument, perms) => {
				let uri = `${document.Links.Self}/acls/requests`;
				return $http.post(uri, perms);
			}
	};

	return documentService;

	function getDocument(documentId:number) {
		let uri = pxUriPathService.resolvePath('/documents/{id}/view-v2', {
			id: documentId
		});

		return $http
			.get<DocumentView>(uri)
			.then(response => response.data);
	}

	function getDocumentUri(document:DocumentView, page:number,  section?:number, viewFormat:DocViewerFormat = DocViewerFormat.IMG, maxWidth?:number){
		let p:ng.IPromise<string>; 
		let uri = document.PageTemplate_v2.replace(document.PageTemplateParam, page.toString());
		let params = {
			v: document.Embedded.Document.VersionNumber,
			section,
			maxWidth
		};
		uri = pxUriPathService.resolvePath(`${uri}/{?v,section,maxWidth}`, params);			

		switch(viewFormat) {
			case DocViewerFormat.SVG:
				const config:ng.IRequestConfig = {
					method: 'GET',
					url: uri,
					headers: {
						'Accept' : 'image/svg+xml,image/jpeg'
					},
					responseType: 'blob'
				};
	
				p = $http<Blob>(config).then(resp => objectHandleRepo.create(resp.data));
				break;
			default:
				p = objectHandleRepo.createFromUri(uri);
				break;	
		}

		return p;
	}


	function getContent(document:DocumentView, viewFormat?:DocViewerFormat){
		let config:ng.IRequestShortcutConfig;
		switch(viewFormat) {
			case DocViewerFormat.TXT:
				config = {
					headers: { 
						Accept: 'text/markdown,text/plain' 
					}
				};
				break
		}

		let uri = document.Embedded.Document.Links.ContentUri_v2;
		return $http
			.get<string>(uri, config)
			.then(response => response.data);
	}

	function getThumbnailUri(document:ShareBaseDocument, size:number = 240){
		let uri = document.Links.ThumbnailUri;
		let params = {
			revision: document.VersionNumber,
			size
		};

		uri = pxUriPathService.resolvePath(`${uri}/{?revision,size}`, params);
		return objectHandleRepo.createFromUri(uri);
	}

	function renameDocument(document:ShareBaseDocument, documentName:string){
		let uri = document.Links.Self;

		return $http
			.patch(uri, {DocumentName:documentName})
			.then(response => response.data as ShareBaseDocument);
	}

	function deleteDocument(document:ShareBaseDocument, purge:boolean = false) {
		let uri = purge ? `/async/documents/${document.DocumentId}?purge=true` : `/documents/${document.DocumentId}?purge=false`;
		uri = pxUriPathService.resolvePath(uri, {
			documentId: document.DocumentId
		});

		return $http.delete(uri);
	}

	function replaceDocument(document:ShareBaseDocument, data:Blob){
		// use a fake File instance that fulfills the interface
		// since IE and Edge don't support using the File constructor
		let file:File = angular.merge(data, {
			name: document.DocumentName
		});

		return pxUploadService.replaceFile(file, document.DocumentId, document.DocumentName, document.Links.Revisions);
	}

	function createDocument(name:string, parentFolder:ShareBaseFolder, extension:string = 'md'){
		let data = new Blob(['\n'], {
			type: 'text/plain'
		});
	
		name += `.${extension}`;

		return pxUploadService.addFile(data, parentFolder.FolderId, name, parentFolder.Links.DocumentsUri);
	}

	function savePermissions(document:ShareBaseDocument, acls) {
		let uri = document.Links.PermissionsUri + '/replace';

		return $http.post(uri, acls);
	}

	function createShare(document:ShareBaseDocument, share:DocumentShare){
		let uri = `${document.Links.Self}/acls/`;
		return $http.post(uri, share);
	}

	function updateShare(document:ShareBaseDocument, share:DocumentShare){
		let uri = `${document.Links.Self}/acls/${share.DocumentACLId}`;
		return $http.patch(uri, share);
	}

	function deleteShare(document:ShareBaseDocument, share:DocumentShare){
		let uri = `${document.Links.Self}/acls/${share.DocumentACLId}`;
		return $http.delete(uri);
	}

	function getShares(document:ShareBaseDocument, compact = true) {
		let uri = document.Links.PermissionsUri;

		if (compact) {
			uri += '?compact=true';
		}

		return $http
			.get(uri)
			.then(response => response.data as ReceivedShareInfo[]);
	}

	function getRights(currentUserBits:number, targetUserBits:number = 0, isInherited:boolean = false){
		var uri = pxUriPathService.resolvePath('/rights-and-restrictions/shareWithUser-v2?acl={acl}&targetAcl={targetAcl}&objType={type}&isInherited={inherited}', {
			acl : currentUserBits,
			type: ObjectType.DOCUMENT,
			targetAcl: targetUserBits,
			inherited: isInherited
		});

		return $http.get(uri).then(resp => resp.data as RightsRestrictions);
	}

	function getLinks(document) {
		let uri = document.Links.SharesUri + '/all';

		return $http
			.get(uri)
			.then(response => response.data as ReceivedLinkShare[]);
	}	

	function getLinkRights(rightBits:number){
		let uri = pxUriPathService.resolvePath('/rights-and-restrictions/shareAsLink-v2?acl={acl}&objType={type}', {
			acl: rightBits,
			type: ObjectType.DOCUMENT
		});

		return $http.get(uri).then(resp => resp.data as RightsRestrictions);
	}

	function getPermissionsForCurrentUser(document) {
		let uri = document.Links.Self + '/permissionsforuser';

		return $http
			.get(uri)
			.then(response => response.data as ReceivedShareInfo[]);
	}
	
	function createLink(document:ShareBaseDocument, share:ServerLinkShare) {
		let uri = document.Links.SharesUri;

		return $http
			.post(uri, share)
			.then(response => response.data as CreatedLinkShare);

	}
	
	function revokeLink(revokedLink:LinkShare){
		return $http.post(revokedLink.Links.RevokeUri,{}).then(resp => resp.data as CreatedLinkShare);
	}
	
	function getMediaInfo(document:ShareBaseDocument){
		let uri = document.Links.Self + '/file/mediainfo';

		return $http
			.get(uri)
			.then(function (response) {
				return response.data;
			});
	}
	function moveDocument(source, destination, copy: boolean = false) {
		let url = destination.Links.DocumentsUri;
		if (copy) {
			url += '?copy=true';
		}
		let reqData = { DocumentId: source.DocumentId };

		return $http
			.patch(url, reqData)
			.then(() => {
				return source;
			});
	}

	function deleteRevision(rev: DocumentRevision) {
		return $http.delete(rev.Links.SelfUri);
	}

	function promote(rev: DocumentRevision) {
		return $http
			.post(rev.Links.PromoteUri, { })
			.then(res => res.data as DocumentRevision);
	}

	function isReceivedLinkShare(link:LinkShare): link is ReceivedLinkShare {
		return angular.isDefined((link as ReceivedLinkShare).ObjectType);
	}
}

DocumentServiceFactory.$inject = [
	'$http',
	'$q',
	'objectHandleRepository',
	'settingsService',
	'pxUriPathService',
	'pxUploadService'
];


// WEBPACK FOOTER //
// ./src/sharebase/services/documentService.ts